/* eslint-disable indent */
import { config } from '@/config';
import { trackAnalyticsEvent } from '@/core/analytics';
import { LoginPayload } from '@/domains/user/ports/payloads';
import { isValidUrl } from '@/helpers';
import logo from '@/static/assets/images/logo_with_text.svg';
import { yupResolver } from '@hookform/resolvers/yup';
import { message } from 'antd';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import {
  LoginForm,
  LoginQRCodeForm,
  MfaSetupForm,
  MfaVerificationForm,
  PasswordlessLoginPanel,
} from '../components';
import { AuthenticationBackground } from '../components/AuthenticationBackground';
import { PASSWORDLESS_EVENTS } from '../constants/passwordless-analytics.constants';
import { authService, AuthUser } from '../services/auth.service';

const schema = yup.object().shape({
  email: yup
    .string()
    .required('Email is required')
    .test('', 'Invalid email or username', (value) => {
      if (!value) return false;
      return config.regex.username.test(value) || config.regex.email.test(value);
    }),
  password: yup.string().required('Password is required'),
});

const UserLoginScreen = () => {
  const t = useTranslation().t;
  const [searchParams, setSearchParams] = useSearchParams();
  const linkToken = searchParams.get('token');
  const isLoginQRCode = !linkToken && searchParams.get('callbackUrl')?.includes('denise');

  // State for authentication flow
  const [authStep, setAuthStep] = useState<
    'login' | 'mfa-setup' | 'mfa-verification' | 'login-qr-code' | 'passwordless' | true
  >(linkToken ? 'passwordless' : 'login');
  const [totpSecret, setTotpSecret] = useState('');
  const [otpAuthUrl, setOtpAuthUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<any>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginPayload>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const execSignin = async (input: LoginPayload): Promise<AuthUser> => {
    const currentUser = config.regex.email.test(input.email)
      ? await authService.signInWithEmailAndPassword(input.email, input.password)
      : await authService.signInWithUsernameAndPassword(input.email, input.password);
    return currentUser;
  };

  const handleSuccessfulLogin = () => {
    const callbackUrl = searchParams.get('callbackUrl');

    if (callbackUrl && isValidUrl(callbackUrl)) {
      window.location.href = callbackUrl;
    } else {
      window.location.href = '/';
    }
  };

  const clearLinkToken = () => {
    if (!searchParams.has('token')) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('token');
    setSearchParams(next, { replace: true });
  };

  const onUsePasswordless = () => {
    trackAnalyticsEvent(PASSWORDLESS_EVENTS.LOGIN_VIEWED);
    setAuthStep('passwordless');
  };

  const onSubmit = async (input: LoginPayload) => {
    setIsLoggingIn(true);
    try {
      await execSignin(input);
      // Navigate on success - check for callbackUrl first
      handleSuccessfulLogin();
    } catch (error: any) {
      const errorMessages: Record<string, string> = {
        'auth/invalid-email': t('wrongLoginCredentials'),
        'auth/user-disabled': t('userDisabled'),
        'auth/user-not-found': t('wrongLoginCredentials'),
        'auth/wrong-password': t('wrongLoginCredentials'),
      };

      if (error.code === 'auth/totp-enrollment-required') {
        const secret = await authService.generateTotpSecret();
        if (secret) {
          // Get the secret in base32 format
          const secretBase32 = secret.secretKey;
          setTotpSecret(secretBase32);

          // Generate OTP Auth URL for QR code
          const appName = encodeURIComponent('MindX App');
          const userEmail = encodeURIComponent(input.email);
          const otpUrl = `otpauth://totp/${appName}:${userEmail}?secret=${secretBase32}&issuer=${appName}`;
          setOtpAuthUrl(otpUrl);

          // Show MFA enrollment section
          setAuthStep('mfa-setup');
          return;
        }
      }

      if (error.code === 'auth/multi-factor-auth-required') {
        // Get the resolver from the error
        const resolver = authService.mfaResolver(error);
        setMfaResolver(resolver);

        // Show MFA verification section
        setAuthStep('mfa-verification');
        return;
      }

      message.error(errorMessages[error.code] || error.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Custom button style to match the image
  const buttonStyle = {
    backgroundColor: '#cc1e1e',
    borderColor: '#cc1e1e',
    borderRadius: '8px',
    height: '40px',
    fontSize: '20px',
    fontWeight: 600,
  };

  const buttonBackStyle = {
    backgroundColor: '#dbdbdb',
    borderRadius: '8px',
    height: '40px',
    fontSize: '20px',
    fontWeight: 500,
  };

  const inputStyle = {
    borderRadius: '8px',
    height: '40px',
  };

  // Verify and enroll TOTP
  const handleVerifyTotp = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      message.error('Please enter a valid verification code');
      return;
    }

    setVerifying(true);
    try {
      await authService.verifyAndEnrollTotp(verificationCode);
      message.success('MFA setup completed successfully');
      setAuthStep('login');
    } catch (error: any) {
      if (error.code === 'auth/totp-secret-not-found') {
        message.error('Your setup session has expired. Please try logging in again.');
      } else if (error.code === 'auth/invalid-verification-code') {
        message.error('Invalid verification code. Please try again.');
      } else {
        message.error(error.message ?? 'Failed to verify code');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      message.error('Please enter a valid verification code');
      return;
    }

    setVerifying(true);
    try {
      // Complete sign-in with MFA verification
      await authService.verifyMfaCode(mfaResolver, verificationCode);
      setAuthStep('login');
      setVerificationCode('');

      // Show success message
      message.success('Login successful');

      // Navigate to callback URL or dashboard
      handleSuccessfulLogin();
    } catch (error: any) {
      console.error('MFA verification failed:', error);
      message.error(error.message || 'Failed to verify code');
    } finally {
      setVerifying(false);
    }
  };

  // Determine content to render based on authentication step
  let content;

  switch (authStep) {
    case 'mfa-setup':
      content = (
        <MfaSetupForm
          otpAuthUrl={otpAuthUrl}
          totpSecret={totpSecret}
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          setAuthStep={setAuthStep}
          handleVerifyTotp={handleVerifyTotp}
          verifying={verifying}
          buttonStyle={buttonStyle}
          buttonBackStyle={buttonBackStyle}
          inputStyle={inputStyle}
        />
      );
      break;

    case 'mfa-verification':
      content = (
        <MfaVerificationForm
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          setAuthStep={setAuthStep}
          handleVerifyMfa={handleVerifyMfa}
          verifying={verifying}
          buttonStyle={buttonStyle}
          buttonBackStyle={buttonBackStyle}
          inputStyle={inputStyle}
        />
      );
      break;

    case 'passwordless':
      content = (
        <PasswordlessLoginPanel
          initialLinkToken={linkToken}
          onAuthenticated={handleSuccessfulLogin}
          onBackToPassword={() => {
            clearLinkToken();
            setAuthStep('login');
          }}
          onClearLinkToken={clearLinkToken}
          buttonStyle={buttonStyle}
          buttonBackStyle={buttonBackStyle}
          inputStyle={inputStyle}
        />
      );
      break;

    default:
      content = (
        <LoginForm
          control={control}
          handleSubmit={handleSubmit as any}
          onSubmit={onSubmit}
          errors={errors}
          isLoggingIn={isLoggingIn}
          buttonStyle={buttonStyle}
          inputStyle={inputStyle}
          onUsePasswordless={onUsePasswordless}
        />
      );
  }

  return (
    <div className='flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100 relative'>
      <AuthenticationBackground />
      <div className='w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-md z-100 relative'>
        <div className='flex justify-center mb-6'>
          <img src={logo} alt='MindX Logo' className='h-12' />
        </div>
        {!isLoginQRCode && authStep !== 'passwordless' && (
          <h1 className='text-2xl font-bold text-center'>
            {authStep === 'mfa-setup'
              ? 'MFA Setup'
              : authStep === 'mfa-verification'
              ? 'MFA Verification'
              : t('common.login')}
          </h1>
        )}
        {isLoginQRCode && authStep !== 'passwordless' ? (
          <LoginQRCodeForm
            control={control}
            handleSubmit={handleSubmit as any}
            onSubmit={onSubmit}
            errors={errors}
            isLoggingIn={isLoggingIn}
            buttonStyle={buttonStyle}
            inputStyle={inputStyle}
            onUsePasswordless={onUsePasswordless}
          />
        ) : (
          content
        )}
      </div>
    </div>
  );
};

export default UserLoginScreen;
export { UserLoginScreen };
